<template>
    <div class="board-block">
        <div class="board-sizer">
            <h3>
                <Parallax :effect="'translateX'" :power="-0.0625">
                    {{layout.board.title}}
                </Parallax>
            </h3>
            <nav>
                <button @click="prev()" :class="['prev-button', !prevEnabled && 'is-disabled']" aria-label="Swicth to next news">
                    <IconArrow class="prev-icon" :color="'#000'"/>
                </button>
                <button @click="next()" :class="['next-button', !nextEnabled && 'is-disabled']" aria-label="Swicth to previous news">
                    <IconArrow class="next-icon" :color="'#000'"/>
                </button>
            </nav>
            <div class="board-scroller">
                <div class="board-list" :style="boardStyle" ref="boards">
                    <component
                        v-for="board in layout.board.layout"
                        :key="board.id"
                        :is="getBoardType(board)"
                        :layout="board"
                        class="board"
                    />
                </div>
            </div>
            <div class="footer">
                <div class="footer-sizer">
                    <NewsFeed v-if="layout.board.newsfeed" :layout="layout.board.newsfeed"/>
                    <Cta
                        :type="'router-link'"
                        :to="$trl({
                            name: layout.board.cta.link
                        })"
                        class="link"
                    >
                        {{layout.board.cta.caption}}
                    </Cta>
                </div>
            </div>
        </div>
    </div>
</template>
<script>
import Resize from '@/pure/Resize'
import Drag from '@/pure/Drag'
import Externallink from '@/components/layout/Board/ExternalLink'
import Highlight from '@/components/layout/Board/Highlight'
import NewsFeed from '@/components/layout/Board/NewsFeed'
import Picture from '@/components/layout/Board/Picture'
import Project from '@/components/layout/Board/Project'
import Narrative from '@/components/layout/Board/Narrative'
import Story from '@/components/layout/Board/Story'
import IconArrow from '@/components/IconArrow'
import Cta from '@/components/Cta'
import Parallax from '@/components/animation/Parallax'
export default {
    components: {
        Externallink,
        Highlight,
        NewsFeed,
        Project,
        Narrative,
        Picture,
        IconArrow,
        Story,
        Cta,
        Parallax
    },
    props: {
        layout: {
            type: Object,
            default() {}
        }
    },
    data() {
        return {
            current: 0,
            boardStyle: { transform: 'none' },
            prevEnabled: false,
            nextEnabled: true,
            boardVisible: 1
        }
    },
    created() {
        this.resize = new Resize()
        this.resize.addOnResized(this.onResize)
        this.onResize(this.resize.width)
    },
    mounted() {
        this.drag = new Drag({
            element: this.$refs.boards,
            onDragCb: this.onDrag,
            onMoveCb: this.onMove,
            onDropCb: this.onDrop,
            onSlideCb: this.onSlide
        })
    },
    activated() {
        this.setCurrent(0)
    },
    destroyed() {
        this.resize.removeOnResized(this.onResize)
        this.drag.dispose()
    },
    methods: {
        getBoardType(board) {
            if (board.type === 'project') {
                return 'project'
            } else if (board.category === 'highlight' && board.highlight.pictured) {
                return 'Picture'
            }
            return board.category
        },
        prev() {
            const gap = Math.max(this.boardVisible - 1, 1)
            this.setCurrent(this.current - gap)
        },
        next() {
            const gap = Math.max(this.boardVisible - 1, 1)
            this.setCurrent(this.current + gap)
        },
        setCurrent(val, forceSlide = false) {
            this.current = Math.max(Math.min(val, this.layout.board.layout.length - this.boardVisible), 0)
            if(forceSlide) {
                this.current = Math.round(this.current)
            }
            this.boardStyle = {
                transform: 'translateX(' +(-this.current * this.width) + 'px)'
            }
            this.prevEnabled = this.current > 0
            this.nextEnabled = this.current < this.layout.board.layout.length - this.boardVisible
        },
        onResize(width) {
            const margin = width * 13 / 100
            this.width = width >  576 ? 460 : width
            this.boardVisible = Math.max(Math.floor((width - margin) / this.width), 1)
            this.setCurrent(this.current)
        },
        onDrag() {
            this.$refs.boards.classList.add('is-drag')
        },
        onSlide() {
            this.$refs.boards.classList.remove('is-drag')
            this.$refs.boards.classList.add('is-slide')
        },
        onDrop() {
            this.$refs.boards.classList.remove('is-drag')
            this.$refs.boards.classList.remove('is-slide')
            if(window.innerWidth < 576) {
                this.setCurrent(this.current, true)
            }
        },
        onMove(x) {
            const val = x / 460
            this.setCurrent(this.current - val)
        }
    }
}
</script>
<style lang="sass" scoped>
@import "@/styles/vars.sass"
.board-block
    @include media-breakpoint-up($xxl)
        margin-left: calc((-100vw + 1920px) * 0.5)
        margin-right: calc((-100vw + 1920px) * 0.5)
    @media print
        padding-top: 1.2cm
        padding-bottom: 1.2cm
        page-break-inside: avoid
        break-inside: avoid
        margin-left: 9.6%
        margin-right: 9.6%
.board-sizer
    position: relative
.board-scroller
    overflow: hidden
    padding-top: 17%
    margin-top: -9.25%
    // padding-bottom: 22.625%
    @media print
        padding-top: .6cm
.board-list
    margin-left: 13.3333333333%
    display: inline-flex
    border: 1px solid #e5e5e5
    transition: transform 1s cubic-bezier(.65,0,.25,1)
    will-change: transform
    @include media-breakpoint-down($sm)
        margin-left: 0%
        border: none
    @media print
        display: block
        transform: none !important
        border: none
        .board
            margin-bottom: .6cm
            border: none
        a.board
            // background-color: blue
            &:after
                content: "https://www.soletanche-bachy.com" attr(href)
                font-size: 12px
                font-weight: normal
                display: block
                width: auto
                position: static
                bottom: initial
                top: initial
                right: initial
                left: initial
                margin-top: 0
                padding-top: 0
                margin-bottom: 0
                padding-bottom: 0
    &.is-drag
        pointer-events: none
        transition: none
    &.is-slide
        transition: none
    /deep/ .default,
    /deep/ .hover,
    /deep/ .highlight
        padding-top: 178px
        padding-right: 75px
        padding-bottom: 85px
        padding-left: 85px
        @include media-breakpoint-down($sm)
            padding: 45px 15px 39px 15px
        @media print
            padding: 0
h3
    color: rgba(0, 0, 0, .2)
    font-size: 50px
    font-weight: 500
    line-height: 2.3178
    position: absolute
    line-height: 2.3178
    left: 6.25%
    top: -0.625em
    margin-top: 17%
    transform: translateX(-50%) translateY(-50%) rotate(-90deg)
    z-index: 1
    @include media-breakpoint-down($sm)
        font-size: 24px
        color: #636772
        position: static
        transform: none
        left: initial
        top: initial
        padding-left: 9.6%
        padding-right: 9.6%
        line-height: 1.0465116279
    @media print
        font-size: 28px
        margin-top: 0
        padding-left: 0%
        padding-right: 0%

nav
    position: absolute
    line-height: 2.3178
    right: 6.25%
    display: flex
    top: -4.7916666667vw
    transform: translateY(-100%)
    margin-top: 17%
    @include media-breakpoint-down($sm)
        position: static
        margin-top: 42px
        transform: none
        padding-left: 9.6%
        padding-right: 9.6%
        right: 0
        left: 0
        width: auto
        .prev-button
            margin-right: auto
    @media print
        display: none

.prev-button, .next-button
    user-select: none
    width: 32px
    height: 27px
    cursor: pointer
    opacity: 1
    &.is-disabled
        opacity: 0.15
        cursor: inherit
.next-button
    margin-left: 1.5em
.prev-icon, .next-icon
    width: 100%
    height: 100%
.prev-icon
    transform: scale(-1, 1)
.footer
    // margin-top: -442px
    padding-bottom: 442px
    position: relative
    height: 0
    @include media-breakpoint-down($lg)
        padding-bottom: 17%
        height: auto
        margin-top: 9.6%
    @media print
        padding-bottom: 0
        height: auto
.footer-sizer
    position: absolute
    left: 0
    top: 0
    height: 100%
    width: 100%
    display: flex
    align-items: center
    justify-content: flex-end

    @include media-breakpoint-down($lg)
        position: static
        left: initial
        top: initial
        height: auto
        width: auto
        flex-direction: column
    @media print
        position: static
        left: initial
        top: initial
        height: auto
        width: auto
        display: block
.cta
    border: 1px solid #e5e5e5
    margin-right: 4%
    @include media-breakpoint-down($sm)
        margin-left: auto
        margin-right: auto
</style>
